/**
 * V112-3-2 (BL-40 item 1) — the programs a system is in, on the system page, with "Add to <program>".
 *
 * Reads: GET /api/w/:slug/programs (any member), per started program GET …/programs/:program/boundary (any member),
 * and GET …/today for the viewer's lead programs. Writes: POST …/programs/:program/boundary/systems — owner / admin
 * or that program's lead; the server decides (403 otherwise) and records `boundary.membership_added` in the audit
 * chain (#246 L-3). No new endpoint. A program not started yet, or none at all, links to the Programs page.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card } from '@aigovops/design-system';
import { ApiError, addSystemToBoundary, getProgramBoundary, getToday } from '../../lib/api';
import { usePrograms } from '../../lib/workspace-navigation';
import { addProgramLink, programTitle } from '../../lib/nav-config';
import { queryKeys } from '../../lib/query-keys';
import { AskAdmin } from '../AskAdmin';
import './system-programs.css';

export function SystemPrograms({ slug, systemId, systemName, isAdmin }: {
  slug: string; systemId: string; systemName: string;
  /** Owner or admin. A program's lead can add to that program too (from Today's viewer); the server is the authority. */
  isAdmin: boolean;
}) {
  const prefix = `/w/${slug}/app`;
  const qc = useQueryClient();
  const programs = usePrograms(slug);
  const today = useQuery({ queryKey: queryKeys.today(slug), queryFn: () => getToday(slug), staleTime: 60_000, enabled: !isAdmin });
  const leads = new Set(today.data?.viewer.lead_program_ids ?? []);
  const cards = programs.data?.programs ?? [];
  const boundaries = useQueries({
    queries: cards.map((c) => ({
      queryKey: ['program-boundary', slug, c.program],
      queryFn: () => getProgramBoundary(slug, c.program),
      staleTime: 60_000,
    })),
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);

  const rows = cards.map((c, i) => {
    const q = boundaries[i];
    return {
      program: c.program, title: programTitle(c.program, c.report_type),
      loading: !!q?.isLoading, failed: !!q?.error,
      inIt: q?.data?.boundary.systems.some((s) => s.system_id === systemId) ?? false,
      canAdd: isAdmin || leads.has(c.profile_id),
    };
  });
  const checking = rows.some((r) => r.loading);
  const anyFailed = rows.some((r) => r.failed);
  const member = rows.filter((r) => r.inIt);
  const addable = rows.filter((r) => !r.inIt && !r.loading && !r.failed);
  const startable = addProgramLink(cards.map((c) => ({ program: c.program, periods: [] }))).length > 0;

  const add = async (program: string, title: string) => {
    if (busy) return;
    setBusy(program); setError(null);
    try {
      await addSystemToBoundary(slug, program, { system_id: systemId, reason: 'Added from the system page' });
      await qc.invalidateQueries({ queryKey: ['program-boundary', slug, program] });
      setStatus(`${systemName} is in ${title} now.`);
      // The pressed button leaves the page; keep keyboard users in the card.
      window.setTimeout(() => heading.current?.focus(), 0);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403
        ? `Only an owner, admin or the ${title} lead can add a system to ${title}.`
        : `${systemName} could not be added to ${title}. Try again.`);
    } finally { setBusy(null); }
  };

  return (
    <Card>
      <section aria-labelledby="system-programs-h" className="sp-card">
        <h2 id="system-programs-h" ref={heading} tabIndex={-1} className="sp-h">Programs</h2>
        {programs.isLoading ? (
          <p className="sp-muted">Loading programs…</p>
        ) : programs.error ? (
          <div className="sp-row"><p className="sp-muted">Programs could not load.</p><Button compact variant="secondary" onClick={() => void programs.refetch()}>Retry</Button></div>
        ) : cards.length === 0 ? (
          <p className="sp-line">No program started yet. <Link to={`${prefix}/programs#add`}>Add a program</Link></p>
        ) : (
          <>
            <p className="sp-line">
              {member.length
                ? <>In {member.map((r, i) => <span key={r.program}>{i ? ', ' : ''}<Link to={`${prefix}/programs/${r.program}/scope`}>{r.title}</Link></span>)}</>
                : checking ? <span className="sp-muted">Checking programs…</span>
                : anyFailed ? null
                : <span className="sp-muted">Not in any program yet.</span>}
            </p>
            {addable.length > 0 && (
              <div className="sp-acts">
                {addable.filter((r) => r.canAdd).map((r) => (
                  <Button key={r.program} compact variant="secondary" aria-disabled={busy !== null || undefined}
                    onClick={() => void add(r.program, r.title)}>
                    {busy === r.program ? 'Adding…' : `Add to ${r.title}`}
                  </Button>
                ))}
                {addable.some((r) => !r.canAdd) && (
                  <AskAdmin slug={slug} compact verb={`add it to ${addable.filter((r) => !r.canAdd).map((r) => r.title).join(' or ')}`}
                    what="adds systems to programs" subject={`GAC Pro: add ${systemName} to a program`} lead="An owner, admin or program lead" />
                )}
              </div>
            )}
            {anyFailed && (
              <div className="sp-row">
                <p className="sp-muted">Some programs could not be checked.</p>
                <Button compact variant="secondary" onClick={() => boundaries.forEach((q) => void q.refetch())}>Retry</Button>
              </div>
            )}
            {startable && <p className="sp-line"><Link to={`${prefix}/programs#add`}>Start another program</Link></p>}
          </>
        )}
        {error ? <Alert variant="error">{error}</Alert> : null}
        <span role="status" aria-live="polite" className="sr-only">{status}</span>
      </section>
    </Card>
  );
}
